import referralService from './referralService';

export const ACQUISITION_SESSION_KEY = 'rely_acquisition_session';
export const ACQUISITION_SESSION_PARAM = 'acquisition_session_id';
const ACQUISITION_SESSION_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const ACQUISITION_PARAM_KEYS = [
  'ref',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'content_id',
  'post_id',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'creative_id',
];

const normalizeString = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const getLocation = (override) => override ||
  (typeof window !== 'undefined' && window.location) || { pathname: '/', search: '' };

const getStorage = (override) => override ||
  (typeof window !== 'undefined' && window.sessionStorage) || null;

const getReferrer = (override) => override !== undefined
  ? normalizeString(override)
  : normalizeString(typeof document !== 'undefined' ? document.referrer : null);

const normalizeAcquisitionSessionId = (value) => {
  const normalized = normalizeString(value);
  return normalized && ACQUISITION_SESSION_ID_PATTERN.test(normalized) ? normalized : null;
};

const extractAcquisitionSessionId = (search) =>
  normalizeAcquisitionSessionId(new URLSearchParams(search || '').get(ACQUISITION_SESSION_PARAM));

const buildPath = (location) => {
  const source = new URLSearchParams(location.search || '');
  const sanitized = new URLSearchParams();
  source.forEach((rawValue, key) => {
    if (!ACQUISITION_PARAM_KEYS.includes(key)) return;
    const value = normalizeString(rawValue);
    if (value) sanitized.set(key, value);
  });
  const acquisitionSessionId = extractAcquisitionSessionId(location.search);
  if (acquisitionSessionId) sanitized.set(ACQUISITION_SESSION_PARAM, acquisitionSessionId);
  const query = sanitized.toString();
  return `${location.pathname || '/'}${query ? `?${query}` : ''}`;
};

const extractParams = (search) => {
  const params = new URLSearchParams(search || '');
  return Object.fromEntries(ACQUISITION_PARAM_KEYS.map((key) => [key, normalizeString(params.get(key))]));
};

const buildTouch = (location, capturedAt, referrer) => ({
  landing_path: buildPath(location),
  captured_at: capturedAt,
  referrer,
  ...extractParams(location.search),
});

const normalizeTouch = (value) => {
  if (!value || typeof value !== 'object') return null;
  return {
    landing_path: normalizeString(value.landing_path) || '/',
    captured_at: normalizeString(value.captured_at),
    referrer: normalizeString(value.referrer),
    ...Object.fromEntries(ACQUISITION_PARAM_KEYS.map((key) => [key, normalizeString(value[key])])),
  };
};

const hasAttribution = (touch) => Boolean(touch?.referrer || ACQUISITION_PARAM_KEYS.some((key) => touch?.[key]));

const normalizeStoredSession = (value) => {
  if (!value || typeof value !== 'object') return null;
  const legacyTouch = normalizeTouch(value);
  const firstTouch = normalizeTouch(value.first_touch) || legacyTouch;
  const lastTouch = normalizeTouch(value.last_touch) || legacyTouch;
  if (!firstTouch) return null;
  const acquisitionSessionId = normalizeAcquisitionSessionId(value.acquisition_session_id);
  return {
    ...firstTouch,
    ...(acquisitionSessionId ? { acquisition_session_id: acquisitionSessionId } : {}),
    first_touch: firstTouch,
    last_touch: lastTouch || firstTouch,
  };
};

const generateAcquisitionSessionId = () => {
  const browserCrypto = typeof window !== 'undefined' ? window.crypto : null;
  return normalizeAcquisitionSessionId(browserCrypto?.randomUUID?.());
};

export function readAcquisitionSession(options = {}) {
  const storage = getStorage(options.storage);
  const raw = storage?.getItem(ACQUISITION_SESSION_KEY);
  if (!raw) return null;
  try {
    return normalizeStoredSession(JSON.parse(raw));
  } catch (_error) {
    storage.removeItem(ACQUISITION_SESSION_KEY);
    return null;
  }
}

export function captureAcquisitionSession(options = {}) {
  const storage = getStorage(options.storage);
  const location = getLocation(options.location);
  const stored = readAcquisitionSession({ storage });
  const capturedAt = options.capturedAt || new Date().toISOString();
  const currentTouch = buildTouch(location, capturedAt, getReferrer(options.referrer));
  const firstTouch = stored?.first_touch || currentTouch;
  const lastTouch = hasAttribution(currentTouch) ? currentTouch : stored?.last_touch || currentTouch;
  const acquisitionSessionId = stored?.acquisition_session_id ||
    extractAcquisitionSessionId(location.search) ||
    normalizeAcquisitionSessionId(options.generateSessionId?.()) ||
    generateAcquisitionSessionId();
  const snapshot = {
    ...firstTouch,
    ...(acquisitionSessionId ? { acquisition_session_id: acquisitionSessionId } : {}),
    first_touch: firstTouch,
    last_touch: lastTouch,
  };

  storage?.setItem(ACQUISITION_SESSION_KEY, JSON.stringify(snapshot));
  const referralCode = lastTouch.ref || firstTouch.ref;
  if (referralCode && options.persistReferral !== false) referralService.storeReferralCode(referralCode);
  return snapshot;
}

export function clearAcquisitionSession(options = {}) {
  getStorage(options.storage)?.removeItem(ACQUISITION_SESSION_KEY);
}
